/**
 * Spotify OAuth helpers (Authorization Code + PKCE).
 * Code verifier stays in sessionStorage; backend exchanges the code.
 */

import axios from 'axios';

const VERIFIER_KEY = 'spotify_pkce_verifier';
const STATE_KEY = 'spotify_oauth_state';

export const SPOTIFY_CALLBACK_PATH = '/spotify-callback';
export const SPOTIFY_GREEN = '#1DB954';

function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateCodeVerifier(length = 64) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (v) => charset[v % charset.length]).join('');
}

export async function generateCodeChallenge(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

export function getSpotifyRedirectUri() {
  // Spotify no longer allows "localhost" in redirect URIs — use loopback IP.
  // See https://developer.spotify.com/documentation/web-api/concepts/redirect_uri
  if (typeof window === 'undefined') {
    return `http://127.0.0.1:3000${SPOTIFY_CALLBACK_PATH}`;
  }
  const { protocol, hostname, port } = window.location;
  const host =
    hostname === 'localhost' || hostname === '::1' ? '127.0.0.1' : hostname;
  const portPart = port ? `:${port}` : '';
  return `${protocol}//${host}${portPart}${SPOTIFY_CALLBACK_PATH}`;
}

export function persistPkceSession(verifier, state) {
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
}

export function readPkceSession() {
  return {
    verifier: sessionStorage.getItem(VERIFIER_KEY),
    state: sessionStorage.getItem(STATE_KEY),
  };
}

export function clearPkceSession() {
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}

/**
 * Start Spotify connect: fetch auth URL from backend and redirect.
 */
export async function startSpotifyConnect() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const redirectUri = getSpotifyRedirectUri();

  const { data } = await axios.get('/api/spotify/auth-url', {
    params: {
      code_challenge: challenge,
      redirect_uri: redirectUri,
    },
  });

  if (!data?.auth_url) {
    throw new Error('Failed to get Spotify authorization URL');
  }

  persistPkceSession(verifier, data.state);
  window.location.href = data.auth_url;
  return data;
}

/**
 * Complete OAuth callback by exchanging code via backend.
 */
export async function completeSpotifyCallback({ code, state }) {
  const { verifier, state: storedState } = readPkceSession();
  if (!verifier) {
    throw new Error('Missing PKCE verifier. Please try connecting again.');
  }
  if (storedState && state && storedState !== state) {
    throw new Error('Invalid OAuth state. Please try connecting again.');
  }

  // Public callback remounts before AuthContext sets axios defaults — attach token like GitHub/Microsoft.
  const token = ensureAuthHeader();
  if (!token) {
    throw new Error('You must be signed in to connect Spotify. Please log in and try again.');
  }

  const { data } = await axios.post(
    '/api/spotify/callback',
    {
      code,
      code_verifier: verifier,
      state,
      redirect_uri: getSpotifyRedirectUri(),
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  clearPkceSession();
  return data;
}

function ensureAuthHeader() {
  const token = localStorage.getItem('token');
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
  return token;
}

export async function fetchSpotifyStatus() {
  ensureAuthHeader();
  const { data } = await axios.get('/api/spotify/status');
  return data;
}

export async function refreshSpotifyAccessToken() {
  const { data } = await axios.post('/api/spotify/refresh');
  return data;
}

export async function disconnectSpotify() {
  const { data } = await axios.delete('/api/spotify/disconnect');
  return data;
}

export async function fetchCurrentlyPlaying() {
  ensureAuthHeader();
  const { data } = await axios.get('/api/spotify/currently-playing');
  return data;
}

export async function spotifyPlay(options = {}) {
  ensureAuthHeader();
  const body = {};
  if (options?.context_uri) {
    body.context_uri = options.context_uri;
  }
  const { data } = await axios.put('/api/spotify/player/play', body);
  return data;
}

export async function fetchSpotifyDevices() {
  ensureAuthHeader();
  const { data } = await axios.get('/api/spotify/player/devices');
  return data?.devices || [];
}

export async function transferSpotifyPlayback(deviceId, { play = false } = {}) {
  ensureAuthHeader();
  const { data } = await axios.put('/api/spotify/player/transfer', {
    device_id: deviceId,
    play: Boolean(play),
  });
  return data;
}

/** Normalize open.spotify.com playlist URLs or bare ids into spotify:playlist:… */
export function normalizeSpotifyPlaylistUri(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  if (raw.startsWith('spotify:playlist:')) return raw;
  const fromUrl = raw.match(/playlist\/([a-zA-Z0-9]+)/);
  if (fromUrl) return `spotify:playlist:${fromUrl[1]}`;
  if (/^[a-zA-Z0-9]{10,}$/.test(raw)) return `spotify:playlist:${raw}`;
  return raw;
}

export async function spotifyPause() {
  ensureAuthHeader();
  const { data } = await axios.put('/api/spotify/player/pause');
  return data;
}

export async function spotifyNext() {
  ensureAuthHeader();
  const { data } = await axios.post('/api/spotify/player/next');
  return data;
}

export async function spotifyPrevious() {
  ensureAuthHeader();
  const { data } = await axios.post('/api/spotify/player/previous');
  return data;
}

export async function spotifySeek(positionMs) {
  ensureAuthHeader();
  const { data } = await axios.put('/api/spotify/player/seek', {
    position_ms: Math.max(0, Math.floor(Number(positionMs) || 0)),
  });
  return data;
}

export async function spotifySetVolume(volumePercent) {
  ensureAuthHeader();
  const pct = Math.max(0, Math.min(100, Math.round(Number(volumePercent) || 0)));
  const { data } = await axios.put('/api/spotify/player/volume', {
    volume_percent: pct,
  });
  return data;
}

export async function postListeningActivity(activity) {
  ensureAuthHeader();
  const { data } = await axios.post('/api/spotify/activity', activity);
  return data;
}

export async function fetchTeamListeningActivity() {
  ensureAuthHeader();
  const { data } = await axios.get('/api/spotify/activity');
  return data?.activities || [];
}
